import React from 'react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          <div className="navbar-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn btn-ghost"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/signin')}
              className="btn btn-ghost"
            >
              Sign in
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="btn btn-primary"
            >
              Sign up
            </button>
          </div>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-lg text-gray-600">
            Last Updated: November 2024
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Updated 2 days ago
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-blue-50 rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Table of Contents</h2>
          <ol className="space-y-2">
            {[
              'Introduction',
              'Information We Collect',
              'How We Use Your Information',
              'Data Sharing and Disclosure',
              'Data Security',
              'Your Rights and Choices',
              'Data Retention',
              'Children\'s Privacy',
              'International Data Transfers',
              'Changes to This Policy',
              'Contact Us'
            ].map((item, index) => (
              <li key={index}>
                <a 
                  href={`#section-${index + 1}`} 
                  className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {index + 1}. {item}
                </a>
              </li>
            ))}
          </ol>
        </div>

        {/* Policy Content */}
        <div className="bg-white rounded-xl shadow-md p-8 space-y-12">
          {/* Section 1 */}
          <section id="section-1" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                Welcome to NexSync ("we," "our," or "us"). We are committed to protecting your 
                personal information and your right to privacy. This Privacy Policy explains how 
                we collect, use, disclose, and safeguard your information when you visit our 
                website and use our services.
              </p>
              <p>
                Please read this privacy policy carefully. If you do not agree with the terms 
                of this privacy policy, please do not access the site or use our services.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Personal Information</h3>
                <p className="text-gray-700 mb-3">
                  We collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-700">
                  <li>Register for an account on our platform</li>
                  <li>Express interest in obtaining information about us or our services</li>
                  <li>Participate in activities on the platform</li>
                  <li>Contact us for customer support</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Information Automatically Collected</h3>
                <p className="text-gray-700">
                  We automatically collect certain information when you visit, use, or navigate 
                  the platform. This information does not reveal your specific identity but may 
                  include device and usage information such as:
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Device Information</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>IP address</li>
                      <li>Browser type and version</li>
                      <li>Operating system</li>
                      <li>Device identifiers</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Usage Information</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>Pages visited</li>
                      <li>Time spent on pages</li>
                      <li>Referring website</li>
                      <li>Search terms used</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                We use personal information collected via our platform for a variety of business 
                purposes described below. We process your personal information for these purposes 
                in reliance on our legitimate business interests, in order to enter into or perform 
                a contract with you, with your consent, and/or for compliance with our legal obligations.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Service Delivery</h4>
                  <p className="text-gray-600 text-sm mt-1">
                    To facilitate account creation and authentication, provide our services, and 
                    process transactions.
                  </p>
                </div>
                
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Communication</h4>
                  <p className="text-gray-600 text-sm mt-1">
                    To send administrative information, respond to inquiries, and send marketing 
                    communications (with consent).
                  </p>
                </div>
                
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Security & Safety</h4>
                  <p className="text-gray-600 text-sm mt-1">
                    To protect our services, prevent fraud, and ensure platform security for all users.
                  </p>
                </div>
                
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Improvement</h4>
                  <p className="text-gray-600 text-sm mt-1">
                    To analyze usage trends, improve user experience, and develop new features.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">We may share your data with:</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Service Providers</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Third-party vendors who provide services on our behalf, such as payment 
                      processing, data analysis, and customer service.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900">Business Transfers</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      In connection with any merger, sale of company assets, financing, or 
                      acquisition of all or a portion of our business.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900">Legal Requirements</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      When required by law or to respond to valid legal process, such as a 
                      search warrant, court order, or subpoena.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Important Note</h3>
                <p className="text-yellow-700">
                  We do not sell, rent, or trade your personal information to third parties 
                  for their commercial purposes without your explicit consent.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
            <div className="space-y-6">
              <p className="text-gray-700">
                We have implemented appropriate technical and organizational security measures 
                designed to protect the security of any personal information we process. 
                However, please also remember that we cannot guarantee that the internet itself 
                is 100% secure.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Encryption</h4>
                  <p className="text-gray-600 text-sm">
                    All data transmitted between your device and our servers is encrypted 
                    using TLS/SSL protocols.
                  </p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Access Controls</h4>
                  <p className="text-gray-600 text-sm">
                    Strict access controls limit employee access to your personal information 
                    to those with a need-to-know basis.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section id="section-6" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights and Choices</h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                Depending on your location, you may have certain rights regarding your personal 
                information. These may include:
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-3 text-left">Right</th>
                      <th className="border border-gray-200 px-4 py-3 text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-4 py-3 font-medium">Access</td>
                      <td className="border border-gray-200 px-4 py-3">
                        Request access to your personal information
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-3 font-medium">Correction</td>
                      <td className="border border-gray-200 px-4 py-3">
                        Request correction of inaccurate or incomplete data
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-4 py-3 font-medium">Deletion</td>
                      <td className="border border-gray-200 px-4 py-3">
                        Request deletion of your personal information
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-3 font-medium">Opt-Out</td>
                      <td className="border border-gray-200 px-4 py-3">
                        Opt-out of marketing communications at any time
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p className="text-gray-600 text-sm">
                To exercise these rights, please contact us using the information provided 
                in the "Contact Us" section below.
              </p>
            </div>
          </section>

          {/* Sections 7-11 (Brief Overview) */}
          <div className="space-y-8">
            {[
              {
                id: 7,
                title: "Data Retention",
                content: "We retain your personal information only for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required or permitted by law."
              },
              {
                id: 8,
                title: "Children's Privacy",
                content: "Our services are not directed to individuals under 18. We do not knowingly collect personal information from children under 18. If we become aware that a child under 18 has provided us with personal information, we will take steps to delete such information."
              },
              {
                id: 9,
                title: "International Data Transfers",
                content: "Your information may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those in your jurisdiction."
              },
              {
                id: 10,
                title: "Changes to This Policy",
                content: "We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the \"Last Updated\" date at the top of this policy."
              }
            ].map((section) => (
              <section key={section.id} id={`section-${section.id}`} className="scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {section.id}. {section.title}
                </h2>
                <p className="text-gray-700">{section.content}</p>
              </section>
            ))}
          </div>

          {/* Section 11 */}
          <section id="section-11" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you have questions or comments about this policy, you may contact our 
                Data Protection Officer at:
              </p>
              <div className="space-y-2">
                <p className="font-medium text-gray-900">Email: privacy@nexsync.com</p>
                <p className="font-medium text-gray-900">Phone: +234 800 123 4567</p>
                <p className="font-medium text-gray-900">
                  Address: 123 Innovation Drive, Victoria Island, Lagos, Nigeria
                </p>
              </div>
            </div>
          </section>

          {/* Acceptance */}
          <div className="border-t border-gray-200 pt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Acceptance of Policy</h3>
                <p className="text-gray-600">
                  By using our platform, you signify your acceptance of this Privacy Policy.
                </p>
              </div>
              <div className="text-sm text-gray-500">
                Effective Date: November 1, 2024
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between mt-8">
          <button 
            onClick={() => navigate('/termsofservice')}
            className="btn btn-secondary"
          >
            ← Terms of Service
          </button>
          <button 
            onClick={() => navigate('/cookiepolicy')}
            className="btn btn-primary"
          >
            Cookie Policy →
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="navbar-brand">
                <div className="brand-logo">NS</div>
                <span className="brand-text">NexSync</span>
              </div>
              <p className="footer-description">
                Your privacy is our priority.
              </p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Legal</h4>
                <button onClick={() => navigate('/privacypolicy')} className="footer-link">Privacy Policy</button>
                <button onClick={() => navigate('/termsofservice')} className="footer-link">Terms of Service</button>
                <button onClick={() => navigate('/cookiepolicy')} className="footer-link">Cookie Policy</button>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} NexSync. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;